/** Fixed base URL for parsing relative checkout paths without touching window.location. */
const RELATIVE_PATH_VALIDATION_BASE = "https://nubank.local";

const LEGACY_HOTELS_BOOK_PATH = "/hotels/book/";
const LEGACY_FLIGHTS_BOOK_PATH = "/flights/book/";
const NUPAY_AUTH_CODE_PARAM = "code";
const NUPAY_ERROR_PARAM = "error";
const NUPAY_STATE_PARAM = "state";
const PAYMENT_ENROLLMENT_STATUS_PARAM = "enrollment";

const SAFE_SEARCH_PARAM_KEY_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
// Blocks protocol-relative URLs and basic script injection in query values.
// Safe for URL redirects only — not safe for direct HTML rendering.
const UNSAFE_SEARCH_VALUE_PATTERN = /(?:^\/\/)|(?:^\w+:)|[<>]/;
// Contact values are opaque ciphertext, never navigation targets. Valid base64
// can start with "//", so relax only the protocol-relative URL guard.
const ENCRYPTED_CONTACT_PARAM_KEYS = new Set(["email", "phone"]);
const UNSAFE_ENCRYPTED_CONTACT_VALUE_PATTERN = /(?:^\w+:)|[<>]/;

function sanitizeSearchParamValues(rawSearch: string): string {
  const input = new URLSearchParams(rawSearch);
  const sanitized = new URLSearchParams();

  for (const [key, value] of input.entries()) {
    if (!SAFE_SEARCH_PARAM_KEY_PATTERN.test(key)) {
      continue;
    }
    const unsafeValuePattern = ENCRYPTED_CONTACT_PARAM_KEYS.has(key)
      ? UNSAFE_ENCRYPTED_CONTACT_VALUE_PATTERN
      : UNSAFE_SEARCH_VALUE_PATTERN;
    if (unsafeValuePattern.test(value)) {
      continue;
    }
    sanitized.append(key, value);
  }

  return sanitized.toString();
}

/** Checkout fields used to infer legacy product when returnPath was not persisted. */
export type LegacyCheckoutStateHints = {
  origin?: string;
  destination?: string;
  tripCategory?: string;
  departureDate?: string;
  outgoingFareId?: string;
  returnFareId?: string;
  flightShopProgress?: number;
  flightShopType?: string;
  cartToken?: string;
};

export type LegacyNupayOAuthReturnContext = {
  returnPath?: string;
  returnSearch?: string;
  state?: LegacyCheckoutStateHints;
  /** Used when returnSearch was not persisted (flight NupayState fields). */
  stateSearchFallback?: string;
};

const LODGING_RETURN_SEARCH_KEYS = [
  "lodgingCity",
  "lodgingId",
  "placeId",
] as const;

function parseReturnSearchParams(returnSearch?: string): URLSearchParams {
  if (!returnSearch) {
    return new URLSearchParams();
  }
  const raw = returnSearch.startsWith("?")
    ? returnSearch.slice(1)
    : returnSearch;
  return new URLSearchParams(raw);
}

/** Lodging checkout params live in returnSearch; NupayState often only has cartToken. */
export function hasLodgingLegacyCheckoutHints(returnSearch?: string): boolean {
  const params = parseReturnSearchParams(returnSearch);
  if (LODGING_RETURN_SEARCH_KEYS.some((key) => params.has(key))) {
    return true;
  }
  return (
    params.has("fromDate") &&
    params.has("untilDate") &&
    !params.has("origin") &&
    !params.has("destination")
  );
}

export function hasFlightLegacyCheckoutHints(
  state?: LegacyCheckoutStateHints
): boolean {
  if (!state) {
    return false;
  }
  return !!(
    state.origin ||
    state.destination ||
    state.tripCategory ||
    state.departureDate ||
    state.outgoingFareId ||
    state.returnFareId ||
    state.flightShopProgress != null ||
    state.flightShopType
  );
}

/**
 * Infer `/hotels/book/` vs `/flights/book/` when returnPath was not stored.
 * Prefer lodging returnSearch over flight-shaped NupayState (hotel contexts can
 * carry a spurious tripCategory from shared checkout code).
 */
export function inferLegacyCheckoutReturnPath(
  context: LegacyNupayOAuthReturnContext
): string | null {
  if (hasLodgingLegacyCheckoutHints(context.returnSearch)) {
    return normalizeLegacyBookPath(LEGACY_HOTELS_BOOK_PATH);
  }
  if (hasFlightLegacyCheckoutHints(context.state)) {
    return normalizeLegacyBookPath(LEGACY_FLIGHTS_BOOK_PATH);
  }
  return null;
}

export function resolveLegacyCheckoutReturnPath(
  context: LegacyNupayOAuthReturnContext
): string | null {
  if (context.returnPath && isSafeLegacyReturnPath(context.returnPath)) {
    return normalizeLegacyBookPath(context.returnPath);
  }
  return inferLegacyCheckoutReturnPath(context);
}

const OAUTH_QUERY_PARAMS = [
  PAYMENT_ENROLLMENT_STATUS_PARAM,
  NUPAY_AUTH_CODE_PARAM,
  NUPAY_ERROR_PARAM,
  NUPAY_STATE_PARAM,
] as const;

function normalizeLegacyBookPath(pathname: string): string {
  try {
    const resolved = new URL(pathname, RELATIVE_PATH_VALIDATION_BASE).pathname;
    return resolved.endsWith("/") ? resolved : `${resolved}/`;
  } catch {
    return pathname;
  }
}

const ALLOWED_LEGACY_RETURN_PATHS = new Set([
  normalizeLegacyBookPath(LEGACY_HOTELS_BOOK_PATH),
  normalizeLegacyBookPath(LEGACY_FLIGHTS_BOOK_PATH),
]);

export function isLegacyCheckoutPath(pathname: string): boolean {
  if (!pathname) {
    return false;
  }
  return isSafeLegacyReturnPath(pathname);
}

/**
 * Validates a stored OAuth return path before redirect.
 * Relative path only, no protocol-relative URLs, no traversal, hotels/flights book allowlist.
 */
export function isSafeLegacyReturnPath(pathname: string): boolean {
  if (!pathname || !pathname.startsWith("/") || pathname.startsWith("//")) {
    return false;
  }
  if (pathname.includes("..") || pathname.includes("\\")) {
    return false;
  }
  if (pathname.includes(":") || pathname.includes("@")) {
    return false;
  }

  let resolvedPath: string;
  try {
    resolvedPath = new URL(pathname, RELATIVE_PATH_VALIDATION_BASE).pathname;
  } catch {
    return false;
  }

  if (!resolvedPath.startsWith("/") || resolvedPath.startsWith("//")) {
    return false;
  }

  return ALLOWED_LEGACY_RETURN_PATHS.has(normalizeLegacyBookPath(resolvedPath));
}

export function sanitizeLegacyReturnSearch(rawSearch: string): string {
  const input = new URLSearchParams(
    rawSearch.startsWith("?") ? rawSearch.slice(1) : rawSearch
  );
  for (const key of OAUTH_QUERY_PARAMS) {
    input.delete(key);
  }
  return sanitizeSearchParamValues(input.toString());
}

export function buildLegacyNupayOAuthRedirectUrl(
  context: LegacyNupayOAuthReturnContext,
  oauth: {
    stateParam: string;
    code?: string | null;
    error?: string | null;
  }
): string | null {
  const normalizedPath = resolveLegacyCheckoutReturnPath(context);
  if (!normalizedPath) {
    return null;
  }

  const checkoutSearch = context.returnSearch?.trim()
    ? sanitizeLegacyReturnSearch(context.returnSearch)
    : sanitizeLegacyReturnSearch(context.stateSearchFallback ?? "");

  const restoredParams = new URLSearchParams(checkoutSearch);
  restoredParams.set(NUPAY_STATE_PARAM, oauth.stateParam);
  if (oauth.code) {
    restoredParams.set(NUPAY_AUTH_CODE_PARAM, oauth.code);
  } else if (oauth.error) {
    restoredParams.set(NUPAY_ERROR_PARAM, oauth.error);
  }
  const queryString = restoredParams.toString();
  return queryString ? `${normalizedPath}?${queryString}` : normalizedPath;
}
